import { FC, PropsWithChildren, useMemo } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { SkeletonLoader, Text, TextProps } from '@frontend/design-system';

interface BodyProps {
  bodyStyles?: Interpolation<Theme>;
}

interface BodyNamespace extends FC<PropsWithChildren<BodyProps>> {
  ContentText: typeof ContentText;
  MilestoneText: typeof MilestoneText;
  Loader: typeof Loader;
}

export const Body: BodyNamespace = ({ children, bodyStyles }) => {
  return <div css={[styles.container, bodyStyles]}>{children}</div>;
};

interface ContentTextProps extends TextProps {
  children: string;
}

export const ContentText: FC<ContentTextProps> = ({ children, color = 'white', ...props }) => {
  return (
    <Text as='span' size='small' color={color} {...props}>
      {children}
    </Text>
  );
};

export const MilestoneText: FC<Omit<ContentTextProps, 'children'> & { completed: number; total: number }> = ({
  completed,
  total,
  ...props
}) => {
  const { t } = useTranslation('onboardingDashboard');

  const renderText = useMemo(() => {
    if (completed === 0 || total === 0) {
      return t('Complete your first task to get started!');
    } else if (completed === total) {
      return t('All milestones completed!');
    } else {
      return t('{{completed}} / {{total}} milestones completed', { completed, total });
    }
  }, [completed, total, t]);

  return (
    <ContentText data-testid='milestone-progress-text' {...props}>
      {renderText}
    </ContentText>
  );
};

const Loader: FC = () => {
  return <SkeletonLoader height={18} />;
};

Body.ContentText = ContentText;
Body.MilestoneText = MilestoneText;
Body.Loader = Loader;

const styles = {
  container: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};
  `,
};
